<script lang="ts" context="module">
    export const viewingNotationPage = writable(false)
</script>

<script lang="ts">
    import { page } from "$app/stores"
    import { getMeta, isRenderable, type Renderable } from "@circleofa/acim-engine"
    import Footnotes, { footnotes, footnote } from "@components/footnotes/Footnotes.svelte"
    import KeyedPage from "./KeyedPage.svelte"
    import { goto } from "$app/navigation"
    import { convertRangePathToURLPath } from "@utils/fallback-book-redirection"
    import { acim } from "@utils/acim"
    import { open } from "@components/navigator/Navigator.svelte"
    import { currentPageOrRenderItemPath, movingToTab, setPath } from "@store/pageTracker"
    import Loader from "@components/utilities/Loader.svelte"
    import { writable } from "svelte/store"
    import { onDestroy, onMount } from "svelte"
    import Button from "@components/button/Button.svelte"
    import { readingFrameHeaderHeight } from "@utils/material/responsive"

    onMount(() => ($viewingNotationPage = true))
    onDestroy(() => ($viewingNotationPage = false))

    let renderable: Renderable | null = null
    let errorMessage: string | null = null
    let scrollBehaviour: ScrollBehavior = "auto"

    /**
     * The idea is reset hash on every page change
     */
    let hash = ""

    $: processNotation($page.params.notation)
    function processNotation(notation: string) {
        // Reset
        errorMessage = null
        $footnote = null

        if (!notation) {
            return
        }
        try {
            /**
             * {@link notation} will never include anything after # (because that's handled after routing)
             * Simple test - if the {@link fullURLPath} matches the notation, it means it's a renderable already
             * This converts something like `/book/T-2.I.3` to `/book/T-2.I#T-2.I.3`*
             */
            const fullURLPath = convertRangePathToURLPath(notation)
            if (fullURLPath !== notation) {
                goto("/book/" + fullURLPath)
                return
            }

            const possibleRenderable = acim.find(fullURLPath)
            if (!isRenderable(possibleRenderable) || possibleRenderable === null) {
                // TODO - 404
                throw new Error("Supplied path is not a valid page")
            }

            if (renderable !== possibleRenderable || $movingToTab) {
                renderable = possibleRenderable
            }
            hash = window.location.hash.substring(1)
            $footnotes = renderable.footnotes

            if ($movingToTab) {
                // If we're moving to a tab, the tab
                // path has already been set
                console.log("moving tabs...")
                scrollBehaviour = "auto"
                $movingToTab = false
            } else {
                // We're not moving to a tab, which means
                // we should set the top navigator page
                setPath(renderable.path)
            }
        } catch (e) {
            console.error(e)
            renderable = null
            errorMessage = "Oops! Looks like something went wrong!"
        }
    }

    // Listen for changes in #
    function hashchange({ oldURL, newURL }: HashChangeEvent) {
        // const oldHash = new URL(oldURL).hash.substring(1)
        const newHash = new URL(newURL).hash.substring(1)
        if (hash !== newHash) hash = newHash
    }

    $: tags = renderable !== null ? getMeta(renderable) : null
</script>

<svelte:window on:hashchange={hashchange} />

<svelte:head>
    {#if renderable && tags}
        <title>{tags.title + " - A Course in Miracles CE App"}</title>
        <meta property="og:title" content={tags.metatitle} />
        <meta property="og:description" content={tags.description} />
        <meta property="description" content={tags.description} />
    {/if}
</svelte:head>

{#if errorMessage}
    <div class="flex text-center flex-col items-center p-4 gap-4">
        <h2 class="text-xl">Whoops!</h2>
        <p>
            It looks like your request for <i>{$page.params.notation}</i> did not match any notation in
            the course!
        </p>
        <Button go={() => open()}>Open Menu</Button>
    </div>
{:else if renderable !== null}
    {#key renderable}
        <KeyedPage {scrollBehaviour} {renderable} {hash} />
    {/key}
{:else}
    <Loader />
{/if}

{#if renderable}
    <Footnotes framedTopClearance={`${$readingFrameHeaderHeight}px`} />
{/if}
